import { Query } from '@apollo/client/react/components';
import { SelectWrapper, theme, usePath } from '@arnold/common';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalizedDateWithoutLeadingZeroes, ProductCode } from '@arnold/core';
import { Loading } from '../../components';
import {
  OrganizationQuery,
  OrganizationSettingQuery,
  OrganizationSettingQueryVariables,
  UserQuery,
  UserSysRole,
} from '../../generated/hooks';
import { getOrganizationSettings } from '../../graphql/queries';
import EditionOverviewCard from '../Settings/EditionOverviewCard';
import { PageLayout } from '../../components/Common/PageLayout';
import { TransactionHistory } from './TransactionHistory';
import i18n from '../../translations/i18n';

interface TransactionHistoryScreenProps {
  user: UserQuery['user'];
  organization: NonNullable<OrganizationQuery['organization']>;
}

const TransactionHistoryScreen: FC<TransactionHistoryScreenProps> = (props) => {
  const { t } = useTranslation('settings');
  const { settingsPath } = usePath();
  const [selectedProductVersionId, setSelectedProductVersionId] = useState<string | undefined>(undefined);
  useEffect(() => {
    setSelectedProductVersionId(props.organization.productVersion?.id);
  }, [props.organization.productVersion?.id]);

  if (props.organization) {
    const selectOptions = props.organization.productVersionHistory?.map((productVersion) => ({
      label: `${productVersion.product
        .toLowerCase()
        .replace(/\b\p{L}/gu, (char) => char.toUpperCase())} (${getLocalizedDateWithoutLeadingZeroes(
        new Date(productVersion.validFrom),
        i18n.language,
      )} - ${getLocalizedDateWithoutLeadingZeroes(new Date(productVersion.validTo), i18n.language)})`,
      value: productVersion.id,
    }));
    const selectedProductVersion = selectOptions?.find((option) => option.value === selectedProductVersionId);

    return (
      <Query<OrganizationSettingQuery, OrganizationSettingQueryVariables>
        query={getOrganizationSettings}
        variables={{ organizationId: props.organization.id }}
      >
        {({ loading, error, data }) => {
          if (loading) {
            return <Loading />;
          }
          if (error) {
            return null;
          }
          if (data && data.organization) {
            const organization = data.organization;
            return (
              <PageLayout
                dataIcom={'heading-transaction-history'}
                title={t('transactionHistoryHeader')}
                breadcrumbItems={[{ title: `${t('AuthorizedPage:settings')}`, link: settingsPath.createPath() }]}
                buttons={[
                  <SelectWrapper
                    value={selectedProductVersion}
                    options={selectOptions}
                    onChange={(option: { value: string; label: string }) => setSelectedProductVersionId(option?.value)}
                    menuPlacement="auto"
                    styles={{
                      menu: (provided: any, state: any) => ({
                        ...provided,
                        ...{
                          zIndex: 1000,
                        },
                      }),
                      container: (provided: any) => ({
                        ...provided,
                        width: '300px',
                      }),
                    }}
                    isSearchable={false}
                  />,
                ]}
                buttonsMargin={`${theme.spacing.e} 0 0 ${theme.spacing.k}`}
              >
                {props.organization.productVersionHistory && selectedProductVersionId && (
                  <EditionOverviewCard
                    productHistory={props.organization.productVersionHistory!}
                    productVersion={props.organization.productVersionHistory?.find(
                      (productVersion) => productVersion.id === selectedProductVersionId,
                    )}
                    activeRespondentsInCurrentPeriodCount={data.organization.activeRespondentsInCurrentPeriodCount}
                    isFromHistoryPage={true}
                  />
                )}
                {data.organization.productVersionHistory?.find(
                  (productVersion) => productVersion.id === selectedProductVersionId,
                )?.product === ProductCode.Credits && (
                  <TransactionHistory
                    productId={selectedProductVersionId!}
                    organizationId={organization.id}
                    isSysAdmin={props.user.systemRole === UserSysRole.SysAdmin}
                    productVersion={data.organization.productVersionHistory?.find(
                      (productVersion) => productVersion.id === selectedProductVersionId,
                    )}
                  />
                )}
              </PageLayout>
            );
          }
          return null;
        }}
      </Query>
    );
  }

  return null;
};

export default TransactionHistoryScreen;
